/**
 * Вкладка «Отделы» (Настройки) — менеджер заводит/переименовывает/архивирует
 * отделы, чтобы потом заводить в них персонал.
 *
 * Бэк: GET/POST /v1/staff/departments, PATCH/DELETE /v1/staff/departments/{id}
 * (useStaffDepartments / useCreateDepartment / useUpdateDepartment /
 * useArchiveDepartment).
 *
 * Формы создания/переименования открываются ОТДЕЛЬНЫМ ЭКРАНОМ (форма вместо
 * списка) с кнопками «Сохранить»/«Назад» снизу — feedback_every_form_is_a_separate_screen.
 * Подтверждение архивации остаётся коротким диалогом «Да/Нет».
 */
import { useState } from "react";
import { Button, Card, Skeleton } from "@hsc/ui";
import type { StaffDepartment } from "@hsc/types";
import {
  useStaffDepartments,
  useCreateDepartment,
  useUpdateDepartment,
  useArchiveDepartment,
} from "./useStaff.js";

export interface DepartmentsPanelProps {
  canManage?: boolean;
}

/** Открытая форма: создание нового отдела либо переименование существующего. */
type DeptForm = { mode: "create" } | { mode: "rename"; dept: StaffDepartment } | null;

function DepartmentRow({
  dept,
  canManage,
  onRename,
}: {
  dept: StaffDepartment;
  canManage: boolean;
  onRename: () => void;
}): JSX.Element {
  const archive = useArchiveDepartment();
  const [confirmArchive, setConfirmArchive] = useState(false);

  return (
    <li className="dept-row" data-testid={`dept-row-${dept.id}`}>
      <span className="dept-row__name">{dept.name}</span>
      {dept.status !== "active" && <span className="ta-chip ta-chip--muted">архив</span>}
      {canManage && dept.status === "active" && (
        <span className="dept-row__actions">
          <button type="button" className="staff-btn staff-btn--ghost" data-testid={`dept-rename-${dept.id}`} onClick={onRename}>
            Переименовать
          </button>
          <button type="button" className="staff-btn staff-btn--ghost" data-testid={`dept-archive-${dept.id}`} onClick={() => setConfirmArchive(true)}>
            Архивировать
          </button>
        </span>
      )}

      {confirmArchive && (
        <div className="staff-modal" role="dialog" aria-modal="true" data-testid={`dept-archive-modal-${dept.id}`}>
          <div className="staff-modal__panel">
            <h3 className="staff-modal__title">Архивировать отдел «{dept.name}»?</h3>
            <p className="staff-modal__text">Отдел скроется из активных. Сотрудников нужно перевести в другой отдел.</p>
            <div className="staff-modal__actions">
              <button type="button" className="staff-btn staff-btn--ghost" onClick={() => setConfirmArchive(false)}>Отмена</button>
              <button
                type="button"
                className="staff-btn staff-btn--danger"
                data-testid={`dept-archive-confirm-${dept.id}`}
                disabled={archive.isPending}
                onClick={() => archive.mutate({ id: dept.id }, { onSuccess: () => setConfirmArchive(false) })}
              >
                {archive.isPending ? "Архивируем…" : "Архивировать"}
              </button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

/** Бэк требует уникальный непустой code на тенант — генерируем slug из названия. */
function slugCode(name: string): string {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9a-яё]+/giu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 24);
  return `${base || "dept"}-${Date.now().toString(36)}`;
}

/** Экран формы отдела (создание / переименование) — отдельный вид. */
function DepartmentForm({ form, onClose }: { form: DeptForm; onClose: () => void }): JSX.Element {
  const create = useCreateDepartment();
  const update = useUpdateDepartment();
  const isRename = form?.mode === "rename";
  const [name, setName] = useState(isRename && form ? form.dept.name : "");

  const pending = create.isPending || update.isPending;
  const isError = create.isError || update.isError;
  const canSubmit = name.trim() !== "" && !pending && (!isRename || (form && name.trim() !== form.dept.name));

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (name.trim() === "") return;
    if (isRename && form && form.mode === "rename") {
      if (name.trim() === form.dept.name) {
        onClose();
        return;
      }
      update.mutate({ id: form.dept.id, input: { name: name.trim() } }, { onSuccess: onClose });
      return;
    }
    create.mutate({ name: name.trim(), code: slugCode(name.trim()) }, { onSuccess: onClose });
  };

  return (
    <section data-testid="departments-panel" className="dept-panel">
      <div className="ta-topbar">
        <h1 className="hsc-page__title">{isRename ? "Переименовать отдел" : "Новый отдел"}</h1>
      </div>
      <Card>
        <form className="hsc-stack" onSubmit={submit} data-testid="dept-form">
          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="dept-name">Название отдела</label>
            <input
              id="dept-name"
              className="ta-input"
              data-testid="dept-add-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Название отдела, напр. «Ресепшн»"
              autoFocus
              required
            />
          </div>
          {isError && (
            <p role="alert" className="staff-invite__msg staff-invite__msg--err" data-testid="dept-add-error">
              Не удалось сохранить отдел.
            </p>
          )}
          <div className="menu-form__submit">
            <Button type="submit" disabled={!canSubmit} data-testid="dept-add-submit">
              {pending ? "Сохраняем…" : isRename ? "Сохранить" : "Создать"}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose} data-testid="dept-form-back">
              Назад
            </Button>
          </div>
        </form>
      </Card>
    </section>
  );
}

export function DepartmentsPanel({ canManage = false }: DepartmentsPanelProps): JSX.Element {
  const deptsQ = useStaffDepartments();
  const [form, setForm] = useState<DeptForm>(null);

  // Создание/переименование — ОТДЕЛЬНЫЙ экран (форма вместо списка).
  if (canManage && form !== null) {
    return <DepartmentForm form={form} onClose={() => setForm(null)} />;
  }

  return (
    <section data-testid="departments-panel" className="dept-panel">
      <div className="dept-panel__head">
        <h2 className="staff-profile__section-title">Отделы отеля</h2>
        {canManage && (
          <button type="button" className="staff-btn" data-testid="dept-add-toggle" onClick={() => setForm({ mode: "create" })}>
            + Отдел
          </button>
        )}
      </div>

      {deptsQ.isLoading ? (
        <div className="dept-list">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={44} radius={12} />
          ))}
        </div>
      ) : deptsQ.isError ? (
        <p role="alert" className="staff-empty">Не удалось загрузить отделы.</p>
      ) : (deptsQ.data ?? []).length === 0 ? (
        <p className="staff-empty">Отделов пока нет. Создайте первый отдел.</p>
      ) : (
        <ul className="dept-list" data-testid="dept-list">
          {(deptsQ.data ?? []).map((d) => (
            <DepartmentRow
              key={d.id}
              dept={d}
              canManage={canManage}
              onRename={() => setForm({ mode: "rename", dept: d })}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
